import axios from "axios";
import moment from "moment-timezone";
import React from "react";
import { PiSpinner } from "react-icons/pi";
import ReactModal from "react-modal";
import { useSelector } from "react-redux";
import Config from "../../Config";
import {
  isAdminMarkedRefund,
  isRefundablePayment,
  isRefundedPayment,
} from "../../pages/personal/adminMetrics";
import {
    color_green_dark,
    color_grey_dark,
    color_grey_light,
    color_red_active,
    color_red_default,
    color_red_ultra_light,
    color_white
} from "../../constants/colors";
import { Button } from "../controls/Button/Button";
import { useI18n } from "../../i18n/I18nProvider";
import {
    OnestNormalDefault,
    OnestNormalSmall,
    OnestSemiBoldDefault,
    OnestSemiBoldSmall,
} from "../styled/TextComponents";

const cellStyle = {
  borderBottom: "1px solid #eeeeec",
  padding: "12px 14px",
  textAlign: "left",
  fontSize: 14,
  whiteSpace: "nowrap",
};

const headerCellStyle = {
  ...cellStyle,
  backgroundColor: "#f7f6f4",
  color: "#777",
  fontWeight: 600,
  borderBottom: "1px solid #e6e5e3",
};

const getStatusColor = (clientType) => {
  const colors = {
    active: "#f5fcf8",
    unpaid: "#fff8f7",
    noTariff: "#fffcf1",
    failed: "#fbf8ff",
    expiring: "#fff8f7",
    referred: "#f7fbff",
    referredPaid: "#f5fcf8",
    errorTransaction: "#fbf8ff",
    cancelled: color_white,
    unsubscribed: color_white,
  };
  return colors[clientType] || color_white;
};

const maskCard = (pan) => {
  if (!pan) return "—";
  const digits = String(pan).replace(/\D/g, "");
  return digits.length >= 4 ? `•• ${digits.slice(-4)}` : "••••";
};

export const UsersTable = ({
  users,
  clientType,
  onFetchData,
  canViewFinance = true,
}) => {
  const userData = useSelector((state) => state.user);
  const { t } = useI18n();
  const timeZone = userData?.time_zone || "Europe/Moscow";
  const [editingUser, setEditingUser] = React.useState(null);
  const [tempTime, setTempTime] = React.useState("");
  const [tempDate, setTempDate] = React.useState("");
  const [isWaiting, setIsWaiting] = React.useState(false);
  const [showPaymentsForUser, setShowPaymentsForUser] = React.useState({});
  const [subscriptionEventsByUser, setSubscriptionEventsByUser] =
    React.useState({});
  const [refundTarget, setRefundTarget] = React.useState(null);
  const [refundComment, setRefundComment] = React.useState("");

  const handleDateClick = (user) => {
    setEditingUser(user);
    if (user.paid_until) {
      setTempTime(moment.utc(user.paid_until).tz(timeZone).format("HH:mm"));
      setTempDate(
        moment.utc(user.paid_until).tz(timeZone).format("YYYY-MM-DD"),
      );
    } else {
      const now = moment().tz(timeZone);
      setTempTime(now.format("HH:mm"));
      setTempDate(now.format("YYYY-MM-DD"));
    }
  };

  const handleCloseModal = () => {
    setEditingUser(null);
    setTempTime("");
    setTempDate("");
  };

  const handleSave = async () => {
    if (!editingUser || !tempTime || !tempDate) return;

    const [hours, minutes] = tempTime.split(":");
    const [year, month, day] = tempDate.split("-");

    let newDate = moment().tz(timeZone);
    newDate = newDate.set({
      year: +year,
      month: +month - 1,
      date: +day,
      hour: +hours,
      minute: +minutes,
      second: 0,
      millisecond: 0,
    });

    try {
      setIsWaiting(true);
      await axios.post(
        `${Config.BACKEND_ADDR}/payments/update_paid_until/${editingUser.id}`,
        { paid_until: newDate.utc().format("YYYY-MM-DDTHH:mm:ss") },
      );
      await onFetchData();
      setIsWaiting(false);
      handleCloseModal();
    } catch (err) {
      alert(t("Ошибка при обновлении даты/времени"));
      setIsWaiting(false);
    }
  };

  const handleDeleteUser = async (user) => {
    if (window.confirm(t("Вы уверены, что хотите удалить этого пользователя?"))) {
      try {
        setIsWaiting(true);
        await axios.delete(`${Config.BACKEND_ADDR}/auth/user/${user.id}`);
        await onFetchData();
        setIsWaiting(false);
      } catch (err) {
        alert(t("Ошибка при удалении пользователя"));
        setIsWaiting(false);
      }
    }
  };

  const handleCloseRefundModal = () => {
    setRefundTarget(null);
    setRefundComment("");
  };

  const handleMarkRefund = async () => {
    if (!refundTarget) return;

    try {
      setIsWaiting(true);
      await axios.post(
        `${Config.BACKEND_ADDR}/payments/admin/mark_refund/${refundTarget.payment.id}`,
        {
          user_id: refundTarget.user.id,
          comment: refundComment.trim() || undefined,
          close_access: true,
        },
      );
      await onFetchData();
      handleCloseRefundModal();
    } catch (err) {
      const detail = err.response?.data?.detail;
      alert(
        typeof detail === "string"
          ? detail
          : t("Не удалось отметить возврат. Проверьте, что бэкенд обновлён."),
      );
    } finally {
      setIsWaiting(false);
    }
  };

  const handleUnmarkRefund = async (user, payment) => {
    const amountRub =
      typeof payment?.amount === "number" ? payment.amount / 100 : "—";
    const confirmed = window.confirm(
      t(
        "Отменить пометку возврата?\n\nПлатёж #{{paymentId}} · {{amount}} ₽ · {{date}}\n\nСтатус вернётся в CONFIRMED, платёж снова попадёт в кассу. Доступ пользователя не восстановится автоматически.",
        {
          paymentId: payment.id,
          amount: amountRub,
          date: payment.payment_date?.slice(0, 10) || "—",
        },
      ),
    );
    if (!confirmed) return;

    try {
      setIsWaiting(true);
      await axios.post(
        `${Config.BACKEND_ADDR}/payments/admin/unmark_refund/${payment.id}`,
        { user_id: user.id },
      );
      await onFetchData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      alert(
        typeof detail === "string"
          ? detail
          : t("Не удалось отменить пометку. Проверьте, что бэкенд обновлён."),
      );
    } finally {
      setIsWaiting(false);
    }
  };

  const togglePayments = async (userId) => {
    const willOpen = !showPaymentsForUser[userId];
    setShowPaymentsForUser((prev) => ({
      ...prev,
      [userId]: willOpen,
    }));

    if (willOpen && subscriptionEventsByUser[userId] === undefined) {
      try {
        const response = await axios.get(
          `${Config.BACKEND_ADDR}/auth/users/${userId}/subscription-history`,
        );
        setSubscriptionEventsByUser((prev) => ({
          ...prev,
          [userId]: Array.isArray(response.data) ? response.data : [],
        }));
      } catch {
        setSubscriptionEventsByUser((prev) => ({ ...prev, [userId]: [] }));
      }
    }
  };

  return (
    <div
      style={{
        overflowX: "auto",
        border: "1px solid #eeeeec",
        borderRadius: 16,
      }}
    >
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          background: color_white,
          borderRadius: 16,
          overflow: "hidden",
        }}
      >
        <thead>
          <tr>
            <th style={headerCellStyle}>ID</th>
            <th style={headerCellStyle}>{t("Имя")}</th>
            <th style={headerCellStyle}>Email</th>
            <th style={headerCellStyle}>{t("Телефон")}</th>
            <th style={headerCellStyle}>{t("Тариф")}</th>
            <th style={headerCellStyle}>{t("Оплачено до")}</th>
            <th style={headerCellStyle}>{t("Карта")}</th>
            <th style={headerCellStyle}>{t("Кто привёл")}</th>
            <th style={headerCellStyle}>{t("Его код")}</th>
            <th style={headerCellStyle}>{t("Действия")}</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user, index) => (
            <React.Fragment key={user.id || index}>
              <tr
                style={{
                  backgroundColor: getStatusColor(clientType),
                }}
              >
                <td style={cellStyle}>
                  <OnestNormalSmall>{user.id}</OnestNormalSmall>
                </td>
                <td style={cellStyle}>
                  <OnestNormalDefault>
                    {user.name || user.full_name || "—"}
                  </OnestNormalDefault>
                </td>
                <td style={cellStyle}>
                  <OnestNormalSmall>{user.email || "—"}</OnestNormalSmall>
                </td>
                <td style={cellStyle}>
                  <OnestNormalSmall>
                    {user.phone_number || "—"}
                  </OnestNormalSmall>
                </td>
                <td style={cellStyle}>
                  <OnestNormalSmall>
                    {clientType !== "new" && user.tariff
                      ? user.tariff === "m"
                        ? t("месячный")
                        : t("годовой")
                      : "—"}
                  </OnestNormalSmall>
                </td>
                <td style={cellStyle}>
                  <OnestNormalSmall
                    onClick={() => handleDateClick(user)}
                    style={{
                      cursor: "pointer",
                      color: color_red_default,
                      textDecoration: "underline",
                    }}
                  >
                    {user.paid_until
                      ? moment
                          .utc(user.paid_until)
                          .tz(timeZone)
                          .format("HH:mm YYYY-MM-DD")
                      : t("не указано")}
                  </OnestNormalSmall>
                </td>
                <td style={cellStyle}>
                  <OnestNormalSmall>
                    {user.pan
                      ? `${maskCard(user.pan)}${user.exp_date ? t(" до {{date}}", { date: `${user.exp_date.slice(0, 2)}/${user.exp_date.slice(2, 4)}` }) : ""}`
                      : "—"}
                  </OnestNormalSmall>
                </td>
                <td style={cellStyle}>
                  {user.referrer ? (
                    <div>
                      <OnestNormalSmall>
                        {user.referrer.name || t("без имени")} · {user.referrer.id}
                      </OnestNormalSmall>
                      <OnestNormalSmall style={{ color: color_grey_dark }}>
                        {user.referred_by}
                      </OnestNormalSmall>
                    </div>
                  ) : (
                    <OnestNormalSmall>
                      {user.referred_by || "—"}
                    </OnestNormalSmall>
                  )}
                </td>
                <td style={cellStyle}>
                  <OnestNormalSmall>
                    {user.referal_code || "—"}
                  </OnestNormalSmall>
                </td>
                <td style={cellStyle}>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <Button
                      style={{
                        padding: "4px 8px",
                        fontSize: 12,
                        background: color_red_ultra_light,
                        color: color_red_active,
                      }}
                      onClick={() => handleDeleteUser(user)}
                      disabled={isWaiting}
                    >
                      {t("Удалить")}
                    </Button>
                    <Button
                      style={{
                        padding: "4px 8px",
                        fontSize: 12,
                      }}
                      onClick={() => togglePayments(user.id)}
                      disabled={isWaiting}
                    >
                      {showPaymentsForUser[user.id]
                        ? t("Скрыть")
                        : canViewFinance
                          ? t("Показать платежи")
                          : t("Подробнее")}
                    </Button>
                  </div>
                </td>
              </tr>
              {showPaymentsForUser[user.id] && (
                <tr>
                  <td
                    colSpan={10}
                    style={{ ...cellStyle, backgroundColor: color_white }}
                  >
                    <div style={{ padding: "10px 0" }}>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "6px 18px",
                            marginBottom: 14,
                          }}
                        >
                          <OnestNormalSmall>
                            {t("Привёл:")}{" "}
                            {user.referrer
                              ? `${user.referrer.name || t("без имени")} · ${user.referrer.id} (${user.referred_by})`
                              : user.referred_by || "—"}
                          </OnestNormalSmall>
                          <OnestNormalSmall>
                            {t("Часовой пояс:")} {user.time_zone || "—"}
                          </OnestNormalSmall>
                          <OnestNormalSmall>
                            Rebill ID: {user.rebillid || "—"}
                          </OnestNormalSmall>
                        </div>
                        <OnestSemiBoldSmall>
                          {t("История отмен и возобновлений")}
                        </OnestSemiBoldSmall>
                        {subscriptionEventsByUser[user.id] === undefined ? (
                          <OnestNormalSmall>{t("Загрузка…")}</OnestNormalSmall>
                        ) : subscriptionEventsByUser[user.id].length > 0 ? (
                          subscriptionEventsByUser[user.id].map(
                            (event, eventIndex) => (
                              <OnestNormalSmall
                                key={event.id || eventIndex}
                                style={{ marginTop: 4 }}
                              >
                                {event.event_type === "subscription_cancel"
                                  ? t("Отмена")
                                  : t("Возобновление")}{" "}
                                —{" "}
                                {event.created_at
                                  ? moment
                                      .utc(event.created_at)
                                      .tz(timeZone)
                                      .format("HH:mm YYYY-MM-DD")
                                  : t("дата не указана")}
                              </OnestNormalSmall>
                            ),
                          )
                        ) : (
                          <OnestNormalSmall style={{ marginTop: 4 }}>
                            {t("Событий нет")}
                          </OnestNormalSmall>
                        )}
                        {canViewFinance ? (
                        <>
                        <div
                          style={{
                            height: 1,
                            margin: "12px 0",
                            background: color_grey_light,
                          }}
                        />
                        <OnestSemiBoldSmall>
                          {t("История платежей")}
                        </OnestSemiBoldSmall>
                        {Array.isArray(user?.payments) &&
                        user.payments.length > 0 ? (
                          user.payments.map((payment, paymentIndex) => (
                          <div
                            key={paymentIndex}
                            style={{
                              marginBottom: 10,
                              paddingBottom: 10,
                              borderBottom: `1px solid ${color_grey_light}`,
                            }}
                          >
                            <OnestSemiBoldSmall
                              style={{ color: color_grey_dark }}
                            >
                              ID: {payment.id}
                            </OnestSemiBoldSmall>
                            <OnestNormalSmall>
                              {t("Оплата")} #{payment?.payment_id || "N/A"}:{" "}
                              {typeof payment?.amount === "number"
                                ? payment.amount / 100
                                : "N/A"}{" "}
                              {t("руб.")}
                            </OnestNormalSmall>
                            <OnestSemiBoldSmall>
                              {payment?.payment_date
                                ? moment
                                    .utc(payment.payment_date)
                                    .tz(timeZone)
                                    .format("HH:mm YYYY-MM-DD")
                                : t("дата не указана")}
                            </OnestSemiBoldSmall>
                            <OnestNormalSmall>
                              {t("статус:")} {payment?.status || "N/A"}
                            </OnestNormalSmall>
                            <OnestNormalSmall>
                              {payment?.description || t("Нет описания")}
                            </OnestNormalSmall>
                            {isAdminMarkedRefund(payment) ? (
                              <>
                                <OnestNormalSmall
                                  style={{ color: color_grey_dark, marginTop: 6 }}
                                >
                                  {t("Возврат учтён — не входит в кассу")}
                                </OnestNormalSmall>
                                <Button
                                  style={{
                                    marginTop: 8,
                                    padding: "4px 10px",
                                    fontSize: 12,
                                    background: color_white,
                                    color: color_grey_dark,
                                    border: `1px solid ${color_grey_light}`,
                                  }}
                                  onClick={() => handleUnmarkRefund(user, payment)}
                                  disabled={isWaiting}
                                >
                                  {t("Отменить помётку")}
                                </Button>
                              </>
                            ) : isRefundedPayment(payment) ? (
                              <OnestNormalSmall
                                style={{ color: color_grey_dark, marginTop: 6 }}
                              >
                                {t("Возврат учтён — не входит в кассу")}
                              </OnestNormalSmall>
                            ) : isRefundablePayment(payment) ? (
                              <Button
                                style={{
                                  marginTop: 8,
                                  padding: "4px 10px",
                                  fontSize: 12,
                                  background: "#fff8f7",
                                  color: color_red_active,
                                  border: `1px solid ${color_red_ultra_light}`,
                                }}
                                onClick={() =>
                                  setRefundTarget({ user, payment })
                                }
                                disabled={isWaiting}
                              >
                                {t("Возврат")}
                              </Button>
                            ) : null}
                          </div>
                          ))
                        ) : (
                          <OnestNormalSmall style={{ marginTop: 4 }}>
                            {t("Платежей нет")}
                          </OnestNormalSmall>
                        )}
                        </>
                        ) : null}
                      </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>

      <ReactModal
        isOpen={refundTarget !== null}
        onRequestClose={handleCloseRefundModal}
        style={{
          content: {
            backgroundColor: color_white,
            borderRadius: "16px",
            width: "440px",
            maxWidth: "calc(100vw - 32px)",
            margin: "auto",
            display: "flex",
            flexDirection: "column",
            padding: "20px",
            gap: "12px",
          },
          overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.65)",
            backdropFilter: "blur(8px)",
            zIndex: 1000,
          },
        }}
      >
        {refundTarget && (
          <>
            <OnestSemiBoldDefault>
              {t("Возврат")}
            </OnestSemiBoldDefault>
            <OnestNormalSmall style={{ color: color_grey_dark }}>
              {t(
                "Деньги уже вернули в Тинькофф. Здесь только учёт: платёж станет REFUNDED, доступ закроется, автосписание отключится. Повторного возврата в банке не будет.",
              )}
            </OnestNormalSmall>
            <OnestNormalSmall>
              {refundTarget.user.name || refundTarget.user.full_name} · id{" "}
              {refundTarget.user.id}
            </OnestNormalSmall>
            <OnestNormalSmall>
              {t("Платёж")} #{refundTarget.payment.payment_id || refundTarget.payment.id}:{" "}
              {typeof refundTarget.payment.amount === "number"
                ? refundTarget.payment.amount / 100
                : "—"}{" "}
              ₽ · {refundTarget.payment.status}
            </OnestNormalSmall>
            <div>
              <OnestNormalSmall style={{ marginBottom: 5 }}>
                {t("Комментарий (необязательно)")}
              </OnestNormalSmall>
              <input
                type="text"
                value={refundComment}
                disabled={isWaiting}
                onChange={(event) => setRefundComment(event.target.value)}
                placeholder={t("Возврат по запросу клиента")}
                style={{
                  width: "100%",
                  padding: 8,
                  borderRadius: 4,
                  border: `1px solid ${color_grey_light}`,
                  fontFamily: "Onest",
                  fontSize: 14,
                  boxSizing: "border-box",
                }}
              />
            </div>
            <div
              style={{
                display: "flex",
                gap: 10,
                justifyContent: "flex-end",
                marginTop: 4,
              }}
            >
              <Button onClick={handleCloseRefundModal} disabled={isWaiting}>
                {t("Отмена")}
              </Button>
              <Button
                onClick={handleMarkRefund}
                disabled={isWaiting}
                style={{
                  backgroundColor: color_red_default,
                  color: color_white,
                }}
              >
                {isWaiting ? t("Сохранение…") : t("Подтвердить возврат")}
              </Button>
            </div>
          </>
        )}
      </ReactModal>

      {/* Модальное окно для редактирования даты/времени */}
      <ReactModal
        isOpen={editingUser !== null}
        onRequestClose={handleCloseModal}
        style={{
          content: {
            backgroundColor: color_white,
            borderRadius: "16px",
            width: "400px",
            margin: "auto",
            display: "flex",
            flexDirection: "column",
            padding: "20px",
            gap: "15px",
          },
          overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.65)",
            backdropFilter: "blur(8px)",
            zIndex: 1000,
          },
        }}
      >
        {editingUser && (
          <>
            <OnestSemiBoldDefault style={{ marginBottom: 10 }}>
              {t("Редактирование для пользователя: {{name}} (ID: {{id}})", {
                name: editingUser.name,
                id: editingUser.id,
              })}
            </OnestSemiBoldDefault>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div>
                <OnestNormalSmall style={{ marginBottom: 5 }}>
                  {t("Время:")}
                </OnestNormalSmall>
                <input
                  type="time"
                  disabled={isWaiting}
                  value={tempTime}
                  onChange={(e) => setTempTime(e.target.value)}
                  style={{
                    width: "100%",
                    padding: 8,
                    borderRadius: 4,
                    border: `1px solid ${color_grey_light}`,
                    fontFamily: "Onest",
                    fontSize: 14,
                  }}
                />
              </div>

              <div>
                <OnestNormalSmall style={{ marginBottom: 5 }}>
                  {t("Дата:")}
                </OnestNormalSmall>
                <input
                  type="date"
                  disabled={isWaiting}
                  value={tempDate}
                  onChange={(e) => setTempDate(e.target.value)}
                  style={{
                    width: "100%",
                    padding: 8,
                    borderRadius: 4,
                    border: `1px solid ${color_grey_light}`,
                    fontFamily: "Onest",
                    fontSize: 14,
                  }}
                />
              </div>

              {isWaiting && (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <PiSpinner
                    style={{ color: color_green_dark }}
                    className="loading-spinner"
                    size={20}
                  />
                  <OnestNormalSmall>{t("Сохранение...")}</OnestNormalSmall>
                </div>
              )}
            </div>

            <div
              style={{
                display: "flex",
                gap: 10,
                justifyContent: "flex-end",
                marginTop: 10,
              }}
            >
              <Button
                onClick={handleCloseModal}
                style={{
                  padding: "8px 16px",
                }}
                disabled={isWaiting}
              >
                {t("Отмена")}
              </Button>
              <Button
                onClick={handleSave}
                style={{
                  padding: "8px 16px",
                  backgroundColor: color_red_default,
                  color: color_white,
                }}
                disabled={isWaiting || !tempTime || !tempDate}
              >
                {t("Сохранить")}
              </Button>
            </div>
          </>
        )}
      </ReactModal>
    </div>
  );
};
